import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { useMediaQuery } from '../hooks/useMediaQuery';
import { MEDIA } from '../lib/breakpoints';

import {
  clearInstallPrompt,
  getInstallPrompt,
  subscribeInstallPrompt,
} from './install-prompt-store';
import { isIosSafari, isStandaloneNavigator } from './platform';

export type InstallPlatform = 'native' | 'ios' | 'none';

export interface InstallPromptState {
  /** Already running from the home screen — nothing to offer. */
  isStandalone: boolean;
  /** 'native' = a held prompt we can show; 'ios' = Safari needs the Share hint. */
  platform: InstallPlatform;
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

export function useInstallPrompt(): InstallPromptState {
  const held = useSyncExternalStore(subscribeInstallPrompt, getInstallPrompt, () => null);
  const standaloneMedia = useMediaQuery(MEDIA.standalone);
  const isStandalone = standaloneMedia || isStandaloneNavigator();
  const platform: InstallPlatform = held !== null ? 'native' : isIosSafari() ? 'ios' : 'none';

  const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    const event = getInstallPrompt();
    if (event === null) return 'unavailable';
    await event.prompt();
    const { outcome } = await event.userChoice;
    clearInstallPrompt();
    return outcome;
  }, []);

  return useMemo(
    () => ({ isStandalone, platform, promptInstall }),
    [isStandalone, platform, promptInstall],
  );
}
